<template>
  <div class="document">
    <div class="header">
      <es-header>
        <template #header>
          <div class="header-title">
            Location Information
            <span v-if="personLocations
              .every((location) => location.personTypeName === personLocations[0].personTypeName)"
            >
              ({{ personLocations[0].personTypeName }})
            </span>
          </div>
        </template>
        <template #actions
          v-if="this.$route.path.includes('masterRecord')
            && (!this.isRetired)
            && userHasEditAccessToPersonLocation
            && matchingPersonTypeConcurrentLocAssign"
        >
          <ul>
            <li class="p-narrow no-border">
              <div class="list-content">
                <router-link
                  tabindex="0"
                  title="Add"
                  :to="{
                    name: 'PidAddLocation',
                    query: {
                      retired: isRetired,
                      personType: personLocations[0].personType,
                    },
                  }"
                >
                  <font-awesome-icon
                    :icon="['fas', 'plus']"
                    size="lg"
                    title="Add"
                  />
                </router-link>
              </div>
            </li>
          </ul>
        </template>
      </es-header>
    </div>
    <es-loader
      v-if="loading"
      style="margin: 2rem"
    />
    <es-exception
      v-else-if="loadingError"
      style="padding: 3rem; margin: 0"
      exceptionMessage="There was an issue.
      Please try again or contact your administrator."
    />
    <div v-else class="content">
      <div class="table-wrapper">
        <table class="table">
          <es-sorter
            :columnHeaders="filteredColumnHeaders"
            @updateSorter="updateSorter"
          />
          <person-profile-master-location-info-row
            v-for="(personLocation, index) in personLocations"
            :isRetired="isRetired"
            :key="index"
            :personLocation="personLocation"
            :currentPersonTypeCategory="personLocations[0].personTypeCategory"
            :userHasEditAccessToPersonLocation="userHasEditAccessToPersonLocation"
          />
      </table>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faPlus,
} from '@fortawesome/pro-solid-svg-icons';
import EsException from '@/components/EsException.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsSorter from '@/components/EsSorter.vue';
import PersonProfileMasterLocationInfoRow from '@/views/PersonProfile/PersonProfileMasterLocationInfoRow.vue';
import FieldLabels from '@/mixins/FieldLabels';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faPlus,
);

export default {
  mixins: [
    FieldLabels,
    PermissionManager,
  ],
  components: {
    EsException,
    EsHeader,
    EsLoader,
    EsSorter,
    PersonProfileMasterLocationInfoRow,
  },
  props: {
    isRetired: {
      type: Boolean,
      default: false,
    },
    person: {
      type: Object,
      required: true,
    },
    personLocations: {
      type: Array,
      required: true,
    },
    userHasEditAccessToPersonLocation: {
      type: Boolean,
    },
  },
  data() {
    return {
      loading: false,
      loadingError: false,
      myColumnHeaders: [],
    };
  },
  async created() {
    this.initializeColumnHeaders();
  },
  computed: {
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
    ...mapGetters('lookups', [
      'activeGrades',
      'activePersonTypes',
      'activeSources',
      'activeTitles',
    ]),
    ...mapGetters('fieldLabelsModule', [
      'getFieldEnabledForPersonType',
      'getFieldLabelByPersonType',
    ]),
    filteredColumnHeaders() {
      return this.myColumnHeaders.filter(item => item.show);
    },
    matchingPersonTypeConcurrentLocAssign() {
      const temp = this.$store.state.user.userPersonTypes
        .find(a => a.personTypeCode === this.personLocations[0].personType);
      if (temp) {
        return temp.personType.concurrentLocAssign;
      }
      return null;
    },
  },
  methods: {
    initializeColumnHeaders() {
      this.myColumnHeaders = [
        {
          field: null,
          header: 'Update Date',
          show: true,
          sortable: true,
          style: {
            width: '15%',
          },
          title: 'Update Date',
        },
        {
          field: null,
          header: this.fieldLabel('SCHOOL_YEAR'),
          show: this.fieldEnabled('SCHOOL_YEAR'),
          sortable: true,
          style: {
            width: '15%',
          },
          title: 'School Year',
        },
        {
          field: null,
          header: 'Location Info',
          show: true,
          sortable: true,
          style: {
            width: '30%',
          },
          title: 'Location Info',
        },
        {
          field: null,
          header: this.fieldLabel('LOCAL_STUDENT_ID'),
          show: this.fieldEnabled('LOCAL_STUDENT_ID'),
          sortable: true,
          style: {
            width: '10%',
          },
          title: 'ID Info',
        },
        {
          field: null,
          header: this.fieldLabel('ALT_STUDENT_ID'),
          show: this.fieldEnabled('ALT_STUDENT_ID'),
          sortable: true,
          style: {
            width: '10%',
          },
          title: 'Alternate ID',
        },
        {
          field: null,
          header: this.fieldLabel('HIRE_DATE'),
          show: this.personLocations[0].personTypeCategory === 'EMPLOYEE' && this.fieldEnabled('HIRE_DATE'),
          sortable: true,
          style: {
            width: '10%',
          },
          title: 'Hire Date',
        },
        {
          field: null,
          header: this.fieldLabel('JOB_CLASS_CODE'),
          show: this.personLocations[0].personTypeCategory === 'EMPLOYEE' && this.fieldEnabled('JOB_CLASS_CODE'),
          sortable: true,
          style: {
            width: '10%',
          },
          title: 'Job Classification',
        },
        {
          field: null,
          header: this.fieldLabel('SALARY'),
          show: this.personLocations[0].personTypeCategory === 'EMPLOYEE' && this.fieldEnabled('SALARY'),
          sortable: true,
          style: {
            width: '10%',
          },
          title: 'Annual Salary',
        },
        {
          field: null,
          header: this.fieldLabel('GRADE_CODE'),
          show: this.personLocations[0].personTypeCategory === 'STUDENT' && this.fieldEnabled('GRADE_CODE'),
          sortable: true,
          style: {
            width: '10%',
          },
          title: 'Grade',
        },
        {
          field: null,
          header: this.fieldLabel('ACTIVE'),
          show: this.fieldEnabled('ACTIVE'),
          sortable: true,
          style: {
            width: '5%',
          },
          title: this.personLocations[0].personTypeCategory === 'STUDENT' ? 'Active/Inactive Indicator' : 'Status',
        },
        {
          field: null,
          header: 'Actions',
          show: true,
          sortable: false,
          style: {
            width: '5%',
          },
          title: 'Actions',
        },
      ];
    },
    fieldEnabled(code) {
      return this.getFieldEnabledForPersonType(code, 'ASSIGN', this.personLocations[0].personType);
    },
    fieldLabel(code) {
      return this.getFieldLabelByPersonType(code, 'ASSIGN', this.personLocations[0].personType);
    },
    updateSorter() {
      this.$emit('updateSorter');
    },
  },
};
</script>

<style lang="sass" scoped>
  .list-content
      padding-right: 0.5rem
  .table-wrapper
    overflow-x: auto
</style>
